








































import { CvValue, EditorialProject, Product } from '@/models'
import { ActionTypes } from '@/store/action-types'
import { Utils } from '@/utils'
import {
  CV_VALUE_COUNTRY,
  CV_VALUE_COURSE_LEVEL,
  CV_VALUE_EDUCATION,
  CV_VALUE_LANGUAGE,
  CV_VALUE_PRODUCT_COLLECTION,
  CV_VALUE_PRODUCT_SERIES,
  CV_VALUE_PRODUCT_STATUS_TYPE,
  CV_VALUE_PRODUCT_WORK_TYPE,
  CV_VALUE_REGION,
  CV_VALUE_SUBJECT,
  CV_VALUE_TYPE_PROD,
  DEFAULT_ICON_PRODUCT
} from '@/utils/constants'
import { CvValueUtils } from '@/utils/cv-value-utils'
import { ProductFieldDefinition, ProductFieldRules, loadMaterialTypes, productFieldI18nKey } from '@/utils/product-fields'
import { Component, Prop, Vue } from 'vue-property-decorator'

const EMPTY = '--'

interface DetailField {
  key: string;
  label: string;
  value: string;
  wide?: boolean;
  link?: boolean;
}

interface DetailSection {
  key: string;
  title: string;
  fields: DetailField[];
}

// CV types whose names the detail shows
const CV_TYPES = [
  CV_VALUE_COUNTRY, CV_VALUE_LANGUAGE, CV_VALUE_TYPE_PROD, CV_VALUE_PRODUCT_STATUS_TYPE, CV_VALUE_PRODUCT_WORK_TYPE,
  CV_VALUE_EDUCATION, CV_VALUE_COURSE_LEVEL, CV_VALUE_SUBJECT, CV_VALUE_REGION, CV_VALUE_PRODUCT_COLLECTION,
  CV_VALUE_PRODUCT_SERIES
]

/**
 * Product detail, organized in the same groups as the product form: one per material type, titled
 * with its name. The material type groups follow the same rules as the form, but a field with a
 * value is always shown so no data is hidden.
 */
@Component({
  components: {
    TypeIcon: () => import('@/components/TypeIcon.vue')
  }
})
export default class ProductDetailModal extends Vue {
  @Prop({ type: Object }) product: Product

  public EMPTY = EMPTY
  Utils = Utils

  private loaded = false
  private materialTypes: CvValue[] = []

  get editorialProjects(): EditorialProject[] {
    return this.$store.getters.editorialProjects
  }

  get project(): EditorialProject | undefined {
    return this.editorialProjects.find(item => item.id === this.product.editorialProject)
  }

  get metadata(): { [key: string]: unknown } {
    return (this.product && this.product.metadata) || {}
  }

  get sections(): DetailSection[] {
    const rules = new ProductFieldRules(this.materialTypes, this.project)
    const project = this.project
    const field = (key: string, labelKey: string, value: string, extra: Partial<DetailField> = {}): DetailField =>
      ({ key, label: this.$t(labelKey) as string, value: value || EMPTY, ...extra })
    const formLabel = (name: string) => `upsert_product_modal_component.${name}_label`
    const typeField = (definition: ProductFieldDefinition) => field(
      definition.field, productFieldI18nKey(definition, 'label'), this.typeFieldValue(definition),
      definition.kind === 'url' ? { wide: true, link: true } : {}
    )

    const general = [
      field('type', formLabel('type'), this.cvName(this.product.type, CV_VALUE_TYPE_PROD)),
      field('editorialProject', formLabel('editorial_project'), project ? project.name : this.product.editorialProject || ''),
      field('country', formLabel('country'), this.cvName(this.product.country, CV_VALUE_COUNTRY)),
      field('statusType', 'product_detail_modal.status_type_label', this.cvName(this.product.statusType, CV_VALUE_PRODUCT_STATUS_TYPE)),
      field('organizationalLevel1', formLabel('organizational_level_1'), this.text('organizationalLevel1')),
      field('organizationalLevel2', formLabel('organizational_level_2'), this.text('organizationalLevel2')),
      field('campaign', formLabel('campaign'), this.text('campaign')),
      field('productionCampaign', formLabel('production_campaign'), this.text('productionCampaign')),
      field('language', formLabel('language'), this.shortCodeName('language', CV_VALUE_LANGUAGE)),
      field('region', formLabel('region_label'), this.regionNames(), { wide: true }),
      ...rules.unassignedFields().map(typeField)
    ].filter(item => {
      if (item.key !== 'organizationalLevel1' && item.key !== 'organizationalLevel2') return true
      const levels = project ? project[item.key as 'organizationalLevel1' | 'organizationalLevel2'] : null
      return !!levels || item.value !== EMPTY
    })

    // Fields of a material type group: shown when the project has the type or when they have a value
    const typeSections = rules.materialTypeGroups().map(group => ({
      key: group.codeId,
      title: group.name,
      fields: group.fields.map(typeField).filter(item => rules.isVisible(item.key) || item.value !== EMPTY)
    }))

    const woodwing = [
      field('wwIssueId', formLabel('ww_issue_id'), this.text('wwIssueId')),
      field('wwIssue', formLabel('ww_issue'), this.text('wwIssue')),
      field('wwBrandId', formLabel('ww_brand_id'), this.text('wwBrandId')),
      field('wwBrand', formLabel('ww_brand'), this.text('wwBrand'))
    ]

    const title = (key: string) => this.$t(`upsert_product_modal_component.sections.${key}`) as string
    return [
      { key: 'general', title: title('general'), fields: general },
      ...typeSections,
      { key: 'woodwing', title: title('woodwing'), fields: woodwing }
    ].filter(section => section.fields.length > 0)
  }

  async created() {
    this.$store.dispatch(ActionTypes.LOAD_EDITORIAL_PROJECTS)
    const [materialTypes] = await Promise.all([
      loadMaterialTypes(),
      ...CV_TYPES.map(type => CvValueUtils.getCvValuesByType(type))
    ])
    this.materialTypes = materialTypes
    this.loaded = true
  }

  private text(key: string): string {
    const value = this.metadata[key]
    return value === null || value === undefined ? '' : String(value).trim()
  }

  private typeFieldValue(definition: ProductFieldDefinition): string {
    const { field, kind, cvType } = definition
    if (kind === 'switch') return this.yesNo(field)
    if (kind === 'date') return this.formatDay(this.text(field))
    if (kind !== 'cv' || !cvType) return this.text(field)
    // The name kept when it was saved goes first
    const savedName = definition.nameField ? this.text(definition.nameField) : ''
    if (savedName) return savedName
    if (definition.shortCode) return this.shortCodeName(field, cvType)
    const codeId = definition.onProduct ? (this.product as unknown as Record<string, string>)[field] : this.text(field)
    return this.cvName(codeId, cvType)
  }

  private cvName(codeId: string | null | undefined, type: string): string {
    if (!codeId) return ''
    const cvValue = CvValueUtils.getCvValue(codeId, type)
    return cvValue && cvValue.name ? cvValue.name : codeId
  }

  // Metadata that keeps the CV short code ('pri' for 'edct-pri')
  private shortCodeName(key: string, type: string): string {
    const value = this.text(key)
    return value ? this.cvName(`${type}-${value}`, type).replace(`${type}-`, '') : ''
  }

  private regionNames(): string {
    const codeIds = this.text('region')
    if (!codeIds) return this.text('regionLabel').split(',').join(', ')
    return codeIds.split(',').map(codeId => this.cvName(codeId, CV_VALUE_REGION)).join(', ')
  }

  private yesNo(key: string): string {
    const value = this.metadata[key]
    if (value === undefined || value === null || value === '') return ''
    return this.$t(value === true || value === 'true' ? 'bulk_product_modal.template_yes' : 'bulk_product_modal.template_no') as string
  }

  // 'YYYY-MM-DD' -> 'DD/MM/YYYY'
  private formatDay(value: string): string {
    return value ? value.split('-').reverse().join('/') : ''
  }

  hideModal() {
    this.$bvModal.hide('ProductDetailModal')
  }

  private getCvValueProductIcon(codeId: string) {
    const cvValue = CvValueUtils.getCvValue(codeId, CV_VALUE_TYPE_PROD)
    if (!cvValue || !cvValue.icon) {
      return DEFAULT_ICON_PRODUCT
    }
    return cvValue.icon
  }
}
